/**
 * Cache Management Page
 *
 * Admin interface for monitoring and managing Redis caches.
 * Provides overview and invalidation capabilities.
 */

import { useState } from 'react';
import { RefreshCw, Loader2, Database } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import {
  useCacheOverview,
  useInvalidateNamespace,
  useBulkInvalidate,
} from '@/hooks/use-cache-management';
import {
  RedisStatusCard,
  NamespaceCards,
  BulkInvalidateDialog,
  InvalidateConfirmDialog,
  NamespaceKeysList,
} from '@/components/cache-management';
import type { CacheNamespace } from '@/types/cache';

/**
 * Cache Management Page Component
 */
export function CacheManagementPage() {
  const { toast } = useToast();

  // State for namespace selection and dialogs
  const [selectedNamespace, setSelectedNamespace] = useState<CacheNamespace | null>(null);
  const [invalidateNamespace, setInvalidateNamespace] = useState<CacheNamespace | null>(null);
  const [invalidateDialogOpen, setInvalidateDialogOpen] = useState(false);
  // State for showing all keys in a namespace
  const [showKeysNamespace, setShowKeysNamespace] = useState<CacheNamespace | null>(null);

  // Queries
  const {
    data: overview,
    isLoading: isOverviewLoading,
    error: overviewError,
    refetch: refetchOverview,
  } = useCacheOverview();

  // Mutations
  const invalidateMutation = useInvalidateNamespace();
  const bulkInvalidateMutation = useBulkInvalidate();

  // Handlers
  const handleNamespaceSelect = (namespace: CacheNamespace) => {
    setSelectedNamespace(namespace);
    // Show the keys list for this namespace
    setShowKeysNamespace(namespace);
  };

  const handleCloseKeysList = () => {
    setShowKeysNamespace(null);
  };

  const handleInvalidateClick = (namespace: CacheNamespace) => {
    setInvalidateNamespace(namespace);
    setInvalidateDialogOpen(true);
  };

  const handleInvalidateConfirm = (id?: string) => {
    if (!invalidateNamespace) return;

    invalidateMutation.mutate(
      { namespace: invalidateNamespace, id },
      {
        onSuccess: data => {
          toast({
            title: 'Cache Invalidated',
            description: `${data.keysDeleted} keys deleted from ${invalidateNamespace} cache.`,
          });
          setInvalidateDialogOpen(false);
          setInvalidateNamespace(null);
        },
        onError: error => {
          toast({
            title: 'Invalidation Failed',
            description: error.message || 'Failed to invalidate cache.',
            variant: 'destructive',
          });
        },
      }
    );
  };

  const handleBulkInvalidate = (namespaces: CacheNamespace[]) => {
    bulkInvalidateMutation.mutate(namespaces, {
      onSuccess: data => {
        toast({
          title: 'Bulk Invalidation Complete',
          description: `${data.totalKeysDeleted} total keys deleted across ${namespaces.length} namespaces.`,
        });
      },
      onError: error => {
        toast({
          title: 'Bulk Invalidation Failed',
          description: error.message || 'Failed to invalidate caches.',
          variant: 'destructive',
        });
      },
    });
  };

  // Get key count for selected namespace
  const selectedNamespaceInfo = overview?.namespaces.find(ns => ns.name === invalidateNamespace);
  const keyCount = selectedNamespaceInfo?.count ?? 0;

  // Loading state
  if (isOverviewLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading cache overview...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (overviewError) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <Alert variant="destructive">
          <AlertTitle>Error Loading Cache Data</AlertTitle>
          <AlertDescription>
            {overviewError instanceof Error
              ? overviewError.message
              : 'Failed to load cache overview. Please try again.'}
          </AlertDescription>
        </Alert>
        <Button onClick={() => refetchOverview()} className="mt-4">
          <RefreshCw className="mr-2 h-4 w-4" />
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Database className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Cache Management</h1>
            <p className="text-sm text-muted-foreground">
              Monitor and manage Redis cache namespaces
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetchOverview()}
            disabled={isOverviewLoading}
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${isOverviewLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          {overview && (
            <BulkInvalidateDialog
              namespaces={overview.namespaces}
              onInvalidate={handleBulkInvalidate}
              isInvalidating={bulkInvalidateMutation.isPending}
            />
          )}
        </div>
      </div>

      {/* Redis Status */}
      {overview && <RedisStatusCard stats={overview.redis} />}

      {/* Namespace Cards */}
      {overview && (
        <div>
          <h2 className="text-lg font-semibold mb-4">Cache Namespaces</h2>
          <NamespaceCards
            namespaces={overview.namespaces}
            selectedNamespace={selectedNamespace}
            onSelect={handleNamespaceSelect}
            onInvalidate={handleInvalidateClick}
            isInvalidating={invalidateMutation.isPending}
          />
        </div>
      )}

      {/* Namespace Keys List - shown when a namespace is selected */}
      {showKeysNamespace && (
        <NamespaceKeysList namespace={showKeysNamespace} onClose={handleCloseKeysList} />
      )}

      {/* Invalidate Confirmation Dialog */}
      <InvalidateConfirmDialog
        open={invalidateDialogOpen}
        onOpenChange={setInvalidateDialogOpen}
        namespace={invalidateNamespace}
        keyCount={keyCount}
        onConfirm={handleInvalidateConfirm}
        isInvalidating={invalidateMutation.isPending}
      />
    </div>
  );
}

/**
 * Export as named component for router
 */
export const Component = CacheManagementPage;
